import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from "recharts";
import { Search, Flame, MapPin, Building2, Factory, DollarSign, ChevronLeft, ChevronRight, ShoppingCart, X, CheckCircle2 } from "lucide-react";
import KPICard from "../components/ui/KPICard";
import SEO from "../components/SEO";

function fmt(n) {
  return n == null ? "—" : Number(n).toLocaleString("pt-BR");
}

function fmtBRL(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtWeekLabel(isoDate) {
  if (!isoDate) return "";
  const [, m, d] = isoDate.split("-");
  return `${d}/${m}`;
}

function fmtDateBR(isoDate) {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
}

function formatCnpj(cnpj) {
  if (!cnpj || cnpj.length !== 14) return cnpj || "—";
  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
}

function capitalize(str) {
  if (!str) return "";
  return str.toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
}

function StatsCard({ title, icon, children }) {
  return (
    <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        {icon && <span className="text-[var(--accent)]">{icon}</span>}
        <h2 className="text-lg font-bold text-[var(--text)]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function HBar({ items, labelKey, valueKey, color, valueFmt }) {
  const maxVal = Math.max(...items.map((r) => r[valueKey] || 0), 1);
  const format = valueFmt || ((v) => fmt(v));
  return (
    <div className="space-y-2">
      {items.map((r) => (
        <div key={r[labelKey]} className="flex items-center gap-2">
          <span className="text-xs text-[var(--text)] w-28 truncate" title={r[labelKey]}>
            {capitalize(r[labelKey])}
          </span>
          <div className="flex-1 h-3 bg-[var(--border)] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(r[valueKey] / maxVal) * 100}%`, backgroundColor: color, opacity: 0.65 }}
            />
          </div>
          <span className="text-xs text-[var(--muted)] w-20 text-right">{format(r[valueKey], r)}</span>
        </div>
      ))}
    </div>
  );
}

function PriceChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--card)] border border-[var(--border)] p-3 rounded-xl shadow-lg">
      <p className="text-xs text-[var(--muted)] font-medium mb-1">Semana de {label}</p>
      <p className="text-sm font-bold text-[var(--text)]">{fmtBRL(payload[0].value)}</p>
    </div>
  );
}

function ComprarGasModal({ revenda, onClose }) {
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [endereco, setEndereco] = useState("");
  const [status, setStatus] = useState("form"); // form | submitting | success | error
  const [errorMsg, setErrorMsg] = useState(null);

  const isValid = nome.trim() && telefone.replace(/\D/g, "").length >= 10 && endereco.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || status === "submitting") return;
    setStatus("submitting");
    try {
      await axios.post("/gas/pedidos", {
        cnpj: revenda.cnpj,
        razaoSocial: revenda.razaoSocial,
        municipio: revenda.municipio,
        uf: revenda.uf,
        distribuidora: revenda.distribuidora,
        nomeCliente: nome.trim(),
        telefone: telefone.trim(),
        enderecoEntrega: endereco.trim(),
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.error || err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative bg-[var(--card)] border border-[var(--border)] rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 text-sm animate-in zoom-in-95 duration-200 text-[var(--text)]">
        {status === "success" ? (
          <div className="text-center py-4 space-y-3">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto">
              <CheckCircle2 size={28} className="text-emerald-500" />
            </div>
            <h3 className="text-base font-bold text-[var(--text)]">Pedido enviado!</h3>
            <p className="text-[var(--muted)] leading-relaxed">
              Você vai receber a disponibilidade no seu WhatsApp em até <strong className="text-[var(--text)]">60 minutos</strong>.
            </p>
            <button
              onClick={onClose}
              className="mt-2 bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs px-5 py-2.5 rounded-xl shadow-sm transition-all"
            >
              Fechar
            </button>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-start border-b border-[var(--border)] pb-3">
              <div className="min-w-0">
                <h3 className="text-base font-bold text-[var(--text)]">Comprar Gás</h3>
                <p className="text-xs text-[var(--muted)] mt-0.5 truncate">
                  {capitalize(revenda.razaoSocial)} · {capitalize(revenda.municipio)}, {revenda.uf}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-[var(--muted)] hover:text-[var(--text)] p-1 hover:bg-[var(--accent-soft)] rounded-lg transition-all flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[var(--muted)] mb-1">Nome completo</label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Seu nome"
                  className="w-full px-3 py-2.5 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--muted)] mb-1">Telefone / WhatsApp</label>
                <input
                  type="tel"
                  value={telefone}
                  onChange={(e) => setTelefone(e.target.value)}
                  placeholder="(00) 00000-0000"
                  className="w-full px-3 py-2.5 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--muted)] mb-1">Endereço de entrega</label>
                <textarea
                  value={endereco}
                  onChange={(e) => setEndereco(e.target.value)}
                  placeholder="Rua, número, bairro, cidade"
                  rows={2}
                  className="w-full px-3 py-2.5 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] resize-none"
                  required
                />
              </div>

              {status === "error" && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-500 rounded-lg p-3 text-xs">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={!isValid || status === "submitting"}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm px-5 py-2.5 rounded-xl shadow-sm transition-all"
              >
                {status === "submitting" ? "Enviando..." : "Confirmar Pedido"}
              </button>
              <p className="text-[10px] text-[var(--muted)] text-center">
                Você receberá a disponibilidade e o valor no seu WhatsApp em até 60 minutos.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const PER_PAGE = 24;

export default function GasPage() {
  const [summary, setSummary] = useState(null);
  const [precos, setPrecos] = useState(null);
  const [filtros, setFiltros] = useState({ ufs: [], distribuidoras: [] });

  const [query, setQuery] = useState("");
  const [uf, setUf] = useState("");
  const [distribuidora, setDistribuidora] = useState("");

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderTarget, setOrderTarget] = useState(null);

  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    axios.get("/gas/summary").then((res) => {
      if (res.data?.success) setSummary(res.data.data);
    }).catch(() => {});
    axios.get("/gas/filtros").then((res) => {
      if (res.data?.success) setFiltros(res.data.data);
    }).catch(() => {});
    axios.get("/gas/precos/resumo").then((res) => {
      if (res.data?.success) setPrecos(res.data.data);
    }).catch(() => {});
  }, []);

  const fetchRevendas = useCallback((params) => {
    const id = ++requestIdRef.current;
    setLoading(true);
    axios.get("/gas/revendas", { params })
      .then((res) => {
        if (requestIdRef.current !== id) return;
        if (res.data?.success) {
          setResults(res.data.data);
          setTotal(res.data.total);
          setTotalPages(res.data.totalPages);
        }
        setError(null);
      })
      .catch((err) => {
        if (requestIdRef.current !== id) return;
        setError(err.response?.data?.error || err.message);
      })
      .finally(() => {
        if (requestIdRef.current === id) setLoading(false);
      });
  }, []);

  // Re-fetch whenever filters/page change (debounced for free-text search)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchRevendas({ q: query, uf, distribuidora, page, limit: PER_PAGE });
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, uf, distribuidora, page, fetchRevendas]);

  const handleFilterChange = (setter) => (e) => {
    setPage(1);
    setter(e.target.value);
  };

  const seoDescription = precos?.precoMedioNacional != null
    ? `Preço médio do botijão de gás (GLP 13kg) no Brasil: ${fmtBRL(precos.precoMedioNacional)}. Compare preços em ${fmt(summary?.total)} revendas de todo o país, com dados atualizados semanalmente pela ANP.`
    : "Compare o preço do botijão de gás de cozinha (GLP 13kg) em milhares de revendas de todo o Brasil, com dados atualizados semanalmente pela ANP.";

  // Product + AggregateOffer structured data so Google can show a price
  // range for the botijão 13kg directly in search results. Only rendered
  // once real min/max prices are loaded — never with placeholder values.
  const jsonLd = (precos?.precoMinimo != null && precos?.precoMaximo != null)
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "Botijão de Gás GLP 13kg",
        description: "Preço do botijão de gás de cozinha (GLP 13kg) coletado semanalmente pela ANP em revendas de todo o Brasil.",
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "BRL",
          lowPrice: precos.precoMinimo,
          highPrice: precos.precoMaximo,
          offerCount: precos.totalPrecosColetados,
          priceValidUntil: precos.latestWeek?.semanaFim,
          availability: "https://schema.org/InStock",
        },
      }
    : null;

  return (
    <div className="space-y-6">
      <SEO
        title="Preço do Gás de Cozinha (Botijão 13kg) por Cidade"
        description={seoDescription}
        path="/gas"
        jsonLd={jsonLd}
      />
      <div className="border-b border-[var(--border)] pb-6">
        <div className="flex items-center gap-2">
          <Flame size={24} className="text-[var(--accent)]" />
          <h1 className="text-3xl font-extrabold tracking-tight text-[var(--text)]">Gás do Povo</h1>
        </div>
        <p className="text-sm text-[var(--muted)] mt-1">
          Revendas de GLP (gás de cozinha) registradas na ANP em todo o Brasil.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* ── Left: Buscar Revenda ── */}
        <div className="lg:col-span-2 bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
          <h2 className="text-lg font-bold text-[var(--text)] mb-3">Buscar Revenda</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
              <input
                type="text"
                value={query}
                onChange={handleFilterChange(setQuery)}
                placeholder="Buscar por nome, CNPJ ou município..."
                className="w-full pl-10 pr-4 py-2.5 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
              />
            </div>
            <select
              value={uf}
              onChange={handleFilterChange(setUf)}
              className="px-3 py-2.5 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            >
              <option value="">Todos os estados</option>
              {filtros.ufs.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <select
              value={distribuidora}
              onChange={handleFilterChange(setDistribuidora)}
              className="px-3 py-2.5 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] max-w-[220px]"
            >
              <option value="">Todas as distribuidoras</option>
              {filtros.distribuidoras.map((d) => <option key={d} value={d}>{capitalize(d)}</option>)}
            </select>
          </div>

          {!loading && (
            <p className="text-xs text-[var(--muted)] mt-3">
              {fmt(total)} resultado{total !== 1 ? "s" : ""}
            </p>
          )}

          {error && (
            <div className="mt-4 bg-red-500/10 border border-red-500/20 text-red-500 rounded-lg p-4 text-sm">
              <strong>Erro ao carregar revendas:</strong> {error}
            </div>
          )}

          {/* Results */}
          <div className="mt-4 space-y-1">
            {results.map((r) => (
              <div
                key={r.codigoIsimp}
                className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-[var(--border)]/20 transition-colors border-b border-[var(--border)] last:border-b-0"
              >
                <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                  <Flame size={16} className="text-orange-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[var(--text)] truncate">{capitalize(r.razaoSocial)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[var(--muted)]">
                    <span className="font-mono">{formatCnpj(r.cnpj)}</span>
                    <span>&middot;</span>
                    <span>{capitalize(r.municipio)}, {r.uf}</span>
                    {r.distribuidora && (
                      <>
                        <span>&middot;</span>
                        <span className="font-semibold text-[var(--accent)]">{capitalize(r.distribuidora)}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                  {r.precoAtual != null && (
                    <div className="text-right">
                      <p className="text-sm font-bold text-[var(--text)]">{fmtBRL(r.precoAtual)}</p>
                      <p className="text-[10px] text-[var(--muted)]">
                        botijão 13kg{r.precoDataColeta && ` · ${fmtDateBR(r.precoDataColeta)}`}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() => setOrderTarget(r)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white transition-colors"
                  >
                    <ShoppingCart size={12} /> Comprar
                  </button>
                </div>
              </div>
            ))}

            {!loading && results.length === 0 && (
              <div className="text-center py-16 text-[var(--muted)]">
                <Flame size={32} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm font-medium">Nenhuma revenda encontrada</p>
              </div>
            )}

            {loading && (
              <div className="text-center py-16 text-[var(--muted)]">
                <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm font-medium">Carregando revendas...</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4 mt-2 border-t border-[var(--border)]">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg text-xs font-semibold bg-[var(--background)] border border-[var(--border)] text-[var(--muted)] disabled:opacity-40 hover:border-orange-500/50 transition-all"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-[var(--muted)] px-2">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg text-xs font-semibold bg-[var(--background)] border border-[var(--border)] text-[var(--muted)] disabled:opacity-40 hover:border-orange-500/50 transition-all"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>

        {/* ── Right: Estatísticas ── */}
        <div className="lg:col-span-1 space-y-6">
          {/* KPIs */}
          {summary && (
            <div className="grid grid-cols-2 gap-3">
              <KPICard label="Revendas Cadastradas" value={fmt(summary.total)} icon={<Flame size={20} />} />
              <KPICard label="Estados" value={fmt(summary.totalUf)} icon={<MapPin size={20} />} />
              <KPICard label="Municípios" value={fmt(summary.totalMunicipios)} icon={<Building2 size={20} />} />
              <KPICard label="Distribuidoras" value={fmt(summary.totalDistribuidoras)} icon={<Factory size={20} />} />
              {precos?.precoMedioNacional != null && (
                <KPICard
                  className="col-span-2"
                  label="Preço Médio (13kg)"
                  value={fmtBRL(precos.precoMedioNacional)}
                  change={precos.variacaoSemanalPct != null ? `${precos.variacaoSemanalPct >= 0 ? "+" : ""}${precos.variacaoSemanalPct.toFixed(1)}%` : null}
                  changeType={precos.variacaoSemanalPct > 0 ? "negative" : precos.variacaoSemanalPct < 0 ? "positive" : "neutral"}
                  icon={<DollarSign size={20} />}
                  tooltip={precos.latestWeek ? `Semana de ${precos.latestWeek.semanaInicio} a ${precos.latestWeek.semanaFim}` : null}
                />
              )}
            </div>
          )}

          {precos && precos.tendencia?.length > 0 && (
            <StatsCard title="Preço Médio Nacional por Semana" icon={<DollarSign size={16} />}>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={precos.tendencia} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <XAxis
                      dataKey="semanaInicio"
                      tickFormatter={fmtWeekLabel}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "var(--muted)", fontSize: 10 }}
                    />
                    <YAxis
                      domain={["auto", "auto"]}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "var(--muted)", fontSize: 10 }}
                      tickFormatter={(v) => v.toFixed(0)}
                      width={32}
                    />
                    <Tooltip content={<PriceChartTooltip />} />
                    <Line type="monotone" dataKey="precoMedio" stroke="#f97316" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </StatsCard>
          )}

          {precos && precos.porEstado?.length > 0 && (
            <StatsCard title="Preço Médio por Estado (mais caros)" icon={<MapPin size={16} />}>
              <HBar
                items={precos.porEstado.slice(0, 12).map((r) => ({ ...r, precoMedioFmt: fmtBRL(r.precoMedio) }))}
                labelKey="estado"
                valueKey="precoMedio"
                color="#f97316"
                valueFmt={(v, r) => r.precoMedioFmt}
              />
            </StatsCard>
          )}

          {summary && (
            <StatsCard title="Revendas por Estado" icon={<MapPin size={16} />}>
              <HBar items={summary.porUf.slice(0, 12)} labelKey="uf" valueKey="total" color="#f97316" />
            </StatsCard>
          )}

          {summary && (
            <StatsCard title="Principais Distribuidoras" icon={<Factory size={16} />}>
              <HBar items={summary.porDistribuidora.slice(0, 12)} labelKey="distribuidora" valueKey="total" color="#0ea5e9" />
            </StatsCard>
          )}
        </div>
      </div>

      {orderTarget && (
        <ComprarGasModal revenda={orderTarget} onClose={() => setOrderTarget(null)} />
      )}
    </div>
  );
}
